import type { Attribution } from '@mist/contracts/promotion'

/**
 * Client attribution capture (R-ANA-01, Section 13.1). All identifiers are
 * pseudonymous and safe to join to purchases without exposing email. First
 * touch is immutable once written; current touch updates on meaningful visits.
 * We store only pseudonymous IDs and approved UTM/referral fields — never full
 * referrer URLs or sensitive query params (Section 13.3).
 */
const ANON_KEY = 'mist.anon_id'
const SESSION_KEY = 'mist.session_id'
const FIRST_TOUCH_KEY = 'mist.attr.first'
const FIRST_TOUCH_ID_KEY = 'mist.attr.first_id'
const CURRENT_TOUCH_ID_KEY = 'mist.attr.current_id'

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `anon-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
}

export function getAnonymousId(): string {
  if (typeof window === 'undefined') return 'server'
  let id = window.localStorage.getItem(ANON_KEY)
  if (!id) {
    id = uuid()
    window.localStorage.setItem(ANON_KEY, id)
  }
  return id
}

export function getSessionId(): string {
  if (typeof window === 'undefined') return 'server'
  let id = window.sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = uuid()
    window.sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

export function getTouchIds(): { readonly firstTouchId: string; readonly currentTouchId: string } {
  if (typeof window === 'undefined') {
    return { firstTouchId: 'server', currentTouchId: 'server' }
  }
  let firstTouchId = window.localStorage.getItem(FIRST_TOUCH_ID_KEY)
  if (!firstTouchId) {
    firstTouchId = `touch_${uuid()}`
    window.localStorage.setItem(FIRST_TOUCH_ID_KEY, firstTouchId)
  }
  let currentTouchId = window.sessionStorage.getItem(CURRENT_TOUCH_ID_KEY)
  if (!currentTouchId) {
    currentTouchId = `touch_${uuid()}`
    window.sessionStorage.setItem(CURRENT_TOUCH_ID_KEY, currentTouchId)
  }
  return { firstTouchId, currentTouchId }
}

function nullableParam(params: URLSearchParams, key: string): string | null {
  const value = params.get(key)?.trim()
  return value ? value.slice(0, 256) : null
}

export function captureAttribution(): Attribution {
  if (typeof window === 'undefined') {
    return {
      landingPath: '/',
      referrerDomain: null,
      utmSource: null,
      utmMedium: null,
      utmCampaign: null,
      utmContent: null,
      utmTerm: null,
      approvedAdId: null,
      referralCampaignId: null,
    }
  }
  const params = new URLSearchParams(window.location.search)
  let referrerDomain: string | null = null
  try {
    referrerDomain = document.referrer ? new URL(document.referrer).hostname : null
  } catch {
    referrerDomain = null
  }
  const attribution: Attribution = {
    landingPath: window.location.pathname.slice(0, 256),
    referrerDomain,
    utmSource: nullableParam(params, 'utm_source'),
    utmMedium: nullableParam(params, 'utm_medium'),
    utmCampaign: nullableParam(params, 'utm_campaign'),
    utmContent: nullableParam(params, 'utm_content'),
    utmTerm: nullableParam(params, 'utm_term'),
    approvedAdId: nullableParam(params, 'ad_id'),
    referralCampaignId: nullableParam(params, 'ref'),
  }
  // First touch is immutable once written.
  if (!window.localStorage.getItem(FIRST_TOUCH_KEY)) {
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attribution))
  }
  return attribution
}
